import type { TrackEvent } from "@/types";

/** 埋点开关：VITE_TRACK_ENABLED=true 时启用 */
const TRACK_ENABLED = import.meta.env.VITE_TRACK_ENABLED === "true";
/** 缓冲队列上限，满则立即 flush */
const BATCH_SIZE = 10;
/** 定时 flush 间隔（毫秒） */
const FLUSH_INTERVAL = 5000;
/** 批量上报接口 */
const TRACK_ENDPOINT = "/api/tools/prompt/track/batch";

let queue: TrackEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let currentUserId: string | undefined;

/** 注入当前用户 ID（AuthContext 初始化时调用） */
export function setTrackUserId(userId?: string): void {
  currentUserId = userId;
}

/**
 * 核心采集函数：自动补全 timestamp / page / userId 等公共字段，
 * 写入缓冲队列，满 BATCH_SIZE 立即 flush，否则启动定时 flush。
 */
export function track(
  event: string,
  category: "page" | "prompt",
  data?: Partial<TrackEvent>,
): void {
  if (!TRACK_ENABLED) return;

  const payload: TrackEvent = {
    event,
    category,
    timestamp: Date.now(),
    page: window.location.pathname,
    userId: currentUserId,
    ...data,
  };

  queue.push(payload);

  if (queue.length >= BATCH_SIZE) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL);
  }
}

/** 批量上报：优先 sendBeacon 保证页面卸载数据不丢，降级 fetch */
function flush(): void {
  if (queue.length === 0) return;

  const batch = queue.splice(0, queue.length);
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  // 上报前用最新 userId 补全：事件入队时用户可能尚未登录完成，
  // flush 时再回填，确保后端能识别用户身份。
  for (const ev of batch) {
    if (!ev.userId) ev.userId = currentUserId;
  }

  const body = JSON.stringify({ events: batch });

  // 优先 sendBeacon，保证页面卸载时不丢数据
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    const ok = navigator.sendBeacon(TRACK_ENDPOINT, blob);
    if (ok) return;
  }

  // 降级 fetch
  fetch(TRACK_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

/**
 * 注册页面卸载监听：visibilitychange=hidden 或 pagehide 时立即 flush。
 * 返回清理函数以便在 React useEffect 中正确移除监听。
 */
export function flushOnHide(): () => void {
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") flush();
  };
  const onPageHide = () => flush();

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);

  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  };
}
